/**
 * Inline SVG glyphs for the four most-picked styling fields.
 *
 * Renders a 28×28 vector icon per option so the user sees a visual
 * representation alongside the dropdown text. Used by StyleIconCard.
 *
 * Each glyph is intentionally minimalist (single-colour line art) so it
 * matches the existing UI palette without adding image assets.
 */
import React from "react";

const C = { stroke: "currentColor", fill: "none", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };

// ─── LAPEL ──────────────────────────────────────────────────────────────
export const LAPEL_GLYPHS = {
  Notch: (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M16 4 L10 14 L13 17 L11 22 L16 28 L21 22 L19 17 L22 14 L16 4 Z" />
      <path d="M10 14 L13 17 M22 14 L19 17" />
    </svg>
  ),
  Peak: (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M16 4 L9 14 L14 17 L11 22 L16 28 L21 22 L18 17 L23 14 L16 4 Z" />
      <path d="M9 14 L14 17 M23 14 L18 17" />
    </svg>
  ),
  Shawl: (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M16 4 C10 8, 8 16, 10 22 L16 28 L22 22 C24 16, 22 8, 16 4 Z" />
    </svg>
  ),
  "Notch Tuxedo": (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M16 4 L10 14 L13 17 L11 22 L16 28 L21 22 L19 17 L22 14 L16 4 Z" />
      <rect x="13" y="9" width="6" height="14" fill="currentColor" opacity="0.3" stroke="none" />
    </svg>
  ),
};

// ─── POCKET ──────────────────────────────────────────────────────────────
export const POCKET_GLYPHS = {
  Flap: (
    <svg viewBox="0 0 32 32" {...C}>
      <rect x="6" y="8" width="20" height="10" rx="1" />
      <path d="M6 12 L26 12 M6 12 L9 17 L23 17 L26 12" />
    </svg>
  ),
  Patch: (
    <svg viewBox="0 0 32 32" {...C}>
      <rect x="7" y="9" width="18" height="14" rx="1" />
    </svg>
  ),
  Besom: (
    <svg viewBox="0 0 32 32" {...C}>
      <rect x="6" y="14" width="20" height="3" />
    </svg>
  ),
  "Double Besom": (
    <svg viewBox="0 0 32 32" {...C}>
      <rect x="6" y="11" width="20" height="2.5" />
      <rect x="6" y="18" width="20" height="2.5" />
    </svg>
  ),
  Jetted: (
    <svg viewBox="0 0 32 32" {...C}>
      <rect x="6" y="14" width="20" height="3" />
      <path d="M6 14 L9 11 L23 11 L26 14 M6 17 L9 20 L23 20 L26 17" opacity="0.5" />
    </svg>
  ),
  "Ticket Pocket": (
    <svg viewBox="0 0 32 32" {...C}>
      <rect x="6" y="10" width="20" height="8" rx="1" />
      <rect x="18" y="14" width="9" height="5" rx="1" />
    </svg>
  ),
};

// ─── VENT ────────────────────────────────────────────────────────────────
export const VENT_GLYPHS = {
  "No Vent": (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M10 4 L10 28 L22 28 L22 4" />
    </svg>
  ),
  "Single Vent": (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M10 4 L10 28 L22 28 L22 4" />
      <path d="M16 14 L16 28" strokeDasharray="2 2" />
    </svg>
  ),
  "Double Vent": (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M10 4 L10 28 L22 28 L22 4" />
      <path d="M12 16 L12 28 M20 16 L20 28" strokeDasharray="2 2" />
    </svg>
  ),
  "Side Vent": (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M10 4 L10 28 L22 28 L22 4" />
      <path d="M12 18 L12 28 M20 18 L20 28" strokeDasharray="2 2" />
    </svg>
  ),
};

// ─── CUFF (trouser bottom finishing) ─────────────────────────────────────
export const CUFF_GLYPHS = {
  "No Cuff": (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M8 4 L10 28 L22 28 L24 4" />
    </svg>
  ),
  '1" Cuff': (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M8 4 L10 26 L22 26 L24 4" />
      <path d="M10 26 L22 26 M10 28 L22 28" />
    </svg>
  ),
  '1.5" Cuff': (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M8 4 L10 24 L22 24 L24 4" />
      <path d="M10 24 L22 24 M10 26 L22 26 M10 28 L22 28" />
    </svg>
  ),
  '2" Cuff': (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M8 4 L10 22 L22 22 L24 4" />
      <path d="M10 22 L22 22 M10 25 L22 25 M10 28 L22 28" />
    </svg>
  ),
  Sidetab: (
    <svg viewBox="0 0 32 32" {...C}>
      <path d="M8 4 L10 28 L22 28 L24 4" />
      <rect x="4" y="6" width="3" height="8" rx="1" />
    </svg>
  ),
};

const GLYPH_SETS = {
  lapel:  LAPEL_GLYPHS,
  pocket: POCKET_GLYPHS,
  vent:   VENT_GLYPHS,
  cuff:   CUFF_GLYPHS,
};

/**
 * Visual chip grid for a styling field. Renders one card per option, with a
 * tiny SVG glyph + the option label. Clicking selects the value. Acts as a
 * replacement for a plain dropdown when the field has 4-7 stable choices.
 *
 * Props:
 *   kind       "lapel" | "pocket" | "vent" | "cuff"
 *   options    [{ value, label }] — pulled from rc_codes if present
 *   value      currently selected value (string)
 *   onChange   (next) => void
 *   testId     base data-testid for the picker
 */
export default function StyleIconCard({
  kind,
  options,
  value,
  onChange,
  testId = "style-icon-card",
  className = "",
}) {
  const glyphMap = GLYPH_SETS[kind] || {};
  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-2 ${className}`} data-testid={testId}>
      {options.map((opt) => {
        const active = value === opt.value;
        const glyph = glyphMap[opt.label] || glyphMap[opt.value];
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            data-testid={`${testId}-opt-${opt.value}`}
            className={`flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-lg border transition-all ${
              active
                ? "border-amber-500/70 bg-amber-500/10 text-amber-200"
                : "border-white/10 bg-[#0a1628] hover:border-white/20 text-slate-300"
            }`}
          >
            <span className={`w-7 h-7 ${active ? "text-amber-300" : "text-slate-400"}`}>
              {glyph || (
                <svg viewBox="0 0 32 32" {...C}>
                  <circle cx="16" cy="16" r="10" />
                </svg>
              )}
            </span>
            <span className="text-[11px] text-center leading-tight">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export { GLYPH_SETS };
